import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart, formatPrice, parsePrice, calculateShipping } from '../context/CartContext';
import { useT } from '../i18n/LanguageContext';
import { api, ApiError } from '../api/client';

/* ============================================================
   Checkout — /checkout
   Formulário com validação e submissão real à API
   ============================================================ */

const INITIAL = {
  nome: '',
  apelido: '',
  email: '',
  telefone: '',
  morada: '',
  cidade: '',
  codigoPostal: '',
  pais: 'pt',
  nif: '',
  notas: '',
  // Cartão de crédito retirado provisoriamente (ver mais abaixo, na lista
  // de payment-methods) — default passa para "mbway" enquanto isso durar.
  metodo: 'mbway',
  aceitaTermos: false,
};

/* Checksum (mod 11) de um NIF português de 9 dígitos — mesmo algoritmo
   usado no backend (App\Rules\ValidPortugueseNif), para avisar já no
   formulário em vez de só ao emitir a fatura no TOConline. */
function isValidNif(nif) {
  if (!/^\d{9}$/.test(nif)) return false;
  const digits = nif.split('').map(Number);
  const checkDigit = digits[8];
  let sum = 0;
  for (let i = 0; i < 8; i++) {
    sum += digits[i] * (9 - i);
  }
  const remainder = sum % 11;
  const expected = remainder < 2 ? 0 : 11 - remainder;
  return expected === checkDigit;
}

function buildValidator(t) {
  return function validate(form) {
    const errors = {};

    if (!form.nome.trim())               errors.nome = t('checkout.err.name');
    if (!form.apelido.trim())            errors.apelido = t('checkout.err.surname');
    if (!/^\S+@\S+\.\S+$/.test(form.email)) errors.email = t('checkout.err.email');
    if (!/^[0-9\s+()-]{7,}$/.test(form.telefone)) errors.telefone = t('checkout.err.phone');
    if (!form.morada.trim())             errors.morada = t('checkout.err.address');
    if (!form.cidade.trim())             errors.cidade = t('checkout.err.city');
    if (!/^\d{4}-\d{3}$/.test(form.codigoPostal))
      errors.codigoPostal = t('checkout.err.postal');
    if (form.nif.trim() && !isValidNif(form.nif.trim()))
      errors.nif = t('checkout.err.nif');

    if (!form.aceitaTermos)
      errors.aceitaTermos = t('checkout.err.terms');

    return errors;
  };
}

/* Mapeia os campos de erro devolvidos pela API ({ "customer.email": "..." })
   para o estado de erros do formulário (que usa nomes de campo simples). */
function mapApiFieldErrors(fields) {
  const errors = {};
  let generic = '';
  Object.entries(fields || {}).forEach(([field, message]) => {
    if (field.startsWith('customer.')) {
      errors[field.replace('customer.', '')] = message;
    } else {
      generic = message;
    }
  });
  return { errors, generic };
}

export default function Checkout() {
  const t = useT();
  const { items, subtotal, totalItems, clearCart, isValidQuantity, unitsNeededForValidQty, shippingRates } = useCart();
  const navigate = useNavigate();

  const [form, setForm]       = useState(INITIAL);
  const [errors, setErrors]   = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [paymentResult, setPaymentResult] = useState(null);
  // Snapshot do total no momento da submissão — o carrinho é limpo assim
  // que a encomenda é criada, por isso `total` (derivado do carrinho ao
  // vivo) já não pode ser usado depois disso para mostrar o valor pago.
  const [orderTotal, setOrderTotal] = useState(0);

  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null); // { code, discountAmount }
  const [discountChecking, setDiscountChecking] = useState(false);
  const [discountError, setDiscountError] = useState('');

  const empty = items.length === 0 && !success;
  const blockedByMinQty = !empty && !success && !isValidQuantity;

  // Preço fixo por conjunto de 6 garrafas (não por valor de compra) — os
  // valores em si (shippingRates) vêm do admin via GET /shipping-rates
  // (ver CartContext.js). Um código de Portes Grátis dispensa este valor
  // por inteiro (ver mais abaixo) — a matemática do `total` já dá o
  // resultado certo sem precisar de um caso especial, porque o desconto
  // devolvido pelo backend, nesse caso, é exatamente igual ao `shipping`
  // calculado aqui.
  const shipping = calculateShipping(totalItems, form.pais, shippingRates) || 0;
  const discountAmount = discountApplied?.discountAmount || 0;
  const total = subtotal - discountAmount + shipping;
  const validate = buildValidator(t);

  const handleApplyDiscount = async () => {
    const code = discountCodeInput.trim();
    if (!code) return;
    setDiscountChecking(true);
    setDiscountError('');
    try {
      const res = await api.validateDiscountCode(code, subtotal, shipping);
      if (res.valid) {
        setDiscountApplied({ code: code.toUpperCase(), discountAmount: res.discountAmount });
      } else {
        setDiscountApplied(null);
        setDiscountError(t('checkout.discount.invalid'));
      }
    } catch {
      setDiscountApplied(null);
      setDiscountError(t('checkout.discount.invalid'));
    } finally {
      setDiscountChecking(false);
    }
  };

  const handleRemoveDiscount = () => {
    setDiscountApplied(null);
    setDiscountCodeInput('');
    setDiscountError('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    // limpa o erro assim que o utilizador começa a corrigir
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (blockedByMinQty) return;
    const errs = validate(form);
    setErrors(errs);
    setSubmitError('');
    if (Object.keys(errs).length > 0) {
      // scroll até ao primeiro erro
      const first = Object.keys(errs)[0];
      document.getElementsByName(first)[0]?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.createOrder({
        customer: {
          nome: form.nome,
          apelido: form.apelido,
          email: form.email,
          telefone: form.telefone,
          morada: form.morada,
          cidade: form.cidade,
          codigoPostal: form.codigoPostal,
          pais: form.pais,
          nif: form.nif || null,
          notas: form.notas || null,
        },
        items: items.map(item => ({ id: item.id, qty: item.qty })),
        payment: { metodo: form.metodo },
        codigoDesconto: discountApplied?.code || null,
      });

      setOrderTotal(total);
      clearCart();

      if (response.payment?.method === 'cartao' && response.payment?.paymentUrl) {
        window.location.href = response.payment.paymentUrl;
        return;
      }

      setPaymentResult(response.payment);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_discount_code') {
        // Pode ter deixado de ser válido entre a verificação e a submissão
        // (ex. outra pessoa esgotou os usos entretanto) — a encomenda não
        // chegou a ser criada, por isso é seguro só remover o código e
        // deixar o cliente tentar de novo sem ele.
        setDiscountApplied(null);
        setSubmitError(t('checkout.discount.noLongerValid'));
      } else if (err instanceof ApiError && err.status === 422) {
        const { errors: fieldErrors, generic } = mapApiFieldErrors(err.fields);
        setErrors(fieldErrors);
        setSubmitError(generic || t('checkout.err.generic'));
      } else {
        setSubmitError(t('checkout.err.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Sucesso ─────────────────────────────────────────── */
  if (success) {
    return (
      <div className="page-top">
        <section className="section" style={{ paddingTop: 'calc(var(--nav-height) + var(--sp-16))' }}>
          <div className="container--narrow t-center">
            <div className="checkout-success">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true">
                <circle cx="32" cy="32" r="28" stroke="var(--clr-ouro)" strokeWidth="1.2" fill="none" />
                <path d="M20 32 L28 40 L44 24" stroke="var(--clr-ouro)" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p className="t-label" style={{ color: '#000000', marginTop: 'var(--sp-6)' }}>
                {t('checkout.success.eyebrow')}
              </p>
              <h1 className="t-h1" style={{ marginTop: 'var(--sp-3)' }}>
                {t('checkout.success.title', { name: form.nome })}
              </h1>
              <p
                className="t-body-lg t-light"
                style={{ marginTop: 'var(--sp-5)', marginBottom: 'var(--sp-10)' }}
                /* O lead contém {amount} e {email} — interpolamos com tags <strong> via dangerouslySet, mas é simples concat */
              >
                {t('checkout.success.lead', {
                  amount: formatPrice(orderTotal),
                  email: form.email,
                })}
              </p>

              <PaymentResultInfo t={t} payment={paymentResult} />

              <div className="flex gap-4 justify-center" style={{ flexWrap: 'wrap', marginTop: 'var(--sp-8)' }}>
                <Link to="/" className="btn btn-lg">{t('checkout.success.cta1')}</Link>
                <Link to="/loja" className="btn btn-lg">{t('checkout.success.cta2')}</Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  /* ── Carrinho vazio ──────────────────────────────────── */
  if (empty) {
    return (
      <div className="page-top">
        <section className="section" style={{ paddingTop: 'calc(var(--nav-height) + var(--sp-16))' }}>
          <div className="container--narrow t-center">
            <h1 className="t-h2">{t('checkout.empty.title')}</h1>
            <p className="t-body-lg t-light" style={{ marginTop: 'var(--sp-5)', marginBottom: 'var(--sp-10)' }}>
              {t('checkout.empty.lead')}
            </p>
            <Link to="/loja" className="btn btn-lg">{t('cart.emptyCta')}</Link>
          </div>
        </section>
      </div>
    );
  }

  /* ── Quantidade abaixo do mínimo (caixas de 6) ──────────── */
  if (blockedByMinQty) {
    return (
      <div className="page-top">
        <section className="section" style={{ paddingTop: 'calc(var(--nav-height) + var(--sp-16))' }}>
          <div className="container--narrow t-center">
            <h1 className="t-h2">{t('checkout.minQty.title')}</h1>
            <p className="t-body-lg t-light" style={{ marginTop: 'var(--sp-5)', marginBottom: 'var(--sp-10)' }}>
              {t('checkout.minQty.lead', { count: unitsNeededForValidQty })}
            </p>
            <Link to="/carrinho" className="btn btn-lg">{t('checkout.minQty.cta')}</Link>
          </div>
        </section>
      </div>
    );
  }

  /* ── Formulário ──────────────────────────────────────── */
  return (
    <div className="page-top">
      <section
        className="section--sm page-header"
        style={{ background: 'var(--clr-azul-veludo)', color: '#ffffff' }}
      >
        <div className="container">
          <p className="t-label" style={{ color: '#ffffff', marginBottom: 'var(--sp-4)' }}>
            {t('checkout.eyebrow')}
          </p>
          <h1 className="t-h1" style={{ color: '#ffffff' }}>{t('checkout.title')}</h1>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <form onSubmit={handleSubmit} noValidate>
            <div className="checkout-layout">

              {/* ── Formulário ── */}
              <div className="checkout-form">

                {/* Contacto */}
                <fieldset className="checkout-fieldset">
                  <legend className="t-h4">{t('checkout.contact')}</legend>
                  <div className="form-grid">
                    <Field label={t('checkout.field.name')}    name="nome"    value={form.nome}    onChange={handleChange} error={errors.nome} />
                    <Field label={t('checkout.field.surname')} name="apelido" value={form.apelido} onChange={handleChange} error={errors.apelido} />
                    <Field label={t('checkout.field.email')}   name="email" type="email" value={form.email} onChange={handleChange} error={errors.email} />
                    <Field label={t('checkout.field.phone')}   name="telefone" type="tel" value={form.telefone} onChange={handleChange} error={errors.telefone} />
                  </div>
                </fieldset>

                {/* Morada */}
                <fieldset className="checkout-fieldset">
                  <legend className="t-h4">{t('checkout.address')}</legend>
                  <div className="form-group" style={{ marginBottom: 'var(--sp-6)' }}>
                    <label className="form-label" htmlFor="morada">{t('checkout.field.address')}</label>
                    <input
                      id="morada"
                      name="morada"
                      className="form-input"
                      value={form.morada}
                      onChange={handleChange}
                      placeholder={t('checkout.field.addressPh')}
                    />
                    {errors.morada && <span className="form-error">{errors.morada}</span>}
                  </div>
                  <div className="form-grid">
                    <Field label={t('checkout.field.city')}   name="cidade"       value={form.cidade}       onChange={handleChange} error={errors.cidade} />
                    <Field label={t('checkout.field.postal')} name="codigoPostal" value={form.codigoPostal} onChange={handleChange} error={errors.codigoPostal} placeholder="0000-000" />
                    <div className="form-group">
                      <label className="form-label" htmlFor="pais">{t('checkout.field.country')}</label>
                      <select
                        id="pais"
                        name="pais"
                        className="form-select"
                        value={form.pais}
                        onChange={handleChange}
                      >
                        <option value="pt">{t('checkout.country.pt')}</option>
                        <option value="es">{t('checkout.country.es')}</option>
                        <option value="fr">{t('checkout.country.fr')}</option>
                        <option value="uk">{t('checkout.country.uk')}</option>
                        <option value="de">{t('checkout.country.de')}</option>
                        <option value="other">{t('checkout.country.other')}</option>
                      </select>
                    </div>
                    <Field label={t('checkout.field.nif')} name="nif" value={form.nif} onChange={handleChange} error={errors.nif} placeholder={t('checkout.field.nifPh')} />
                  </div>
                </fieldset>

                {/* Pagamento */}
                <fieldset className="checkout-fieldset">
                  <legend className="t-h4">{t('checkout.payment')}</legend>

                  <div className="payment-methods">
                    {[
                      // Cartão de crédito retirado provisoriamente — comentado,
                      // não apagado, para repor facilmente se precisar.
                      // { id: 'cartao',     label: t('checkout.method.card')      },
                      { id: 'mbway',      label: t('checkout.method.mbway')     },
                      { id: 'multibanco', label: t('checkout.method.multibanco') },
                    ].map(m => (
                      <label
                        key={m.id}
                        className={`payment-method${form.metodo === m.id ? ' payment-method--active' : ''}`}
                      >
                        <input
                          type="radio"
                          name="metodo"
                          value={m.id}
                          checked={form.metodo === m.id}
                          onChange={handleChange}
                        />
                        <span>{m.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Nota do cartão comentada junto com a opção acima.
                  {form.metodo === 'cartao' && (
                    <p className="t-body t-light" style={{ marginTop: 'var(--sp-5)' }}>
                      {t('checkout.method.cardNote')}
                    </p>
                  )}
                  */}

                  {form.metodo === 'mbway' && (
                    <p className="t-body t-light" style={{ marginTop: 'var(--sp-5)' }}>
                      {t('checkout.method.mbwayNote')}
                    </p>
                  )}

                  {form.metodo === 'multibanco' && (
                    <p className="t-body t-light" style={{ marginTop: 'var(--sp-5)' }}>
                      {t('checkout.method.multibancoNote')}
                    </p>
                  )}
                </fieldset>

                {/* Notas */}
                <fieldset className="checkout-fieldset">
                  <legend className="t-h4">{t('checkout.notes')}</legend>
                  <div className="form-group">
                    <textarea
                      name="notas"
                      className="form-textarea"
                      value={form.notas}
                      onChange={handleChange}
                      placeholder={t('checkout.field.notesPh')}
                    />
                  </div>
                </fieldset>

                {/* Termos */}
                <div className="checkout-fieldset">
                  <label className="checkout-terms">
                    <input
                      type="checkbox"
                      name="aceitaTermos"
                      checked={form.aceitaTermos}
                      onChange={handleChange}
                    />
                    <span>{t('checkout.terms')}</span>
                  </label>
                  {errors.aceitaTermos && <span className="form-error">{errors.aceitaTermos}</span>}
                </div>

              </div>

              {/* ── Resumo ── */}
              <aside className="cart-summary checkout-summary">
                <h2 className="t-h4" style={{ marginBottom: 'var(--sp-6)' }}>{t('checkout.summary')}</h2>

                <ul className="checkout-items">
                  {items.map(item => (
                    <li key={item.id} className="checkout-item">
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="checkout-item__img"
                        />
                      )}
                      <span className="checkout-item__qty">{item.qty}×</span>
                      <span className="checkout-item__name">{item.name} {item.year}</span>
                      <span className="checkout-item__price">
                        {formatPrice(parsePrice(item.price) * item.qty)}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="divider" style={{ margin: 'var(--sp-5) 0' }} />

                <div className="cart-summary__row">
                  <span className="t-body">{t('cart.subtotal')}</span>
                  <span className="t-body">{formatPrice(subtotal)}</span>
                </div>
                <div className="cart-summary__row">
                  <span className="t-body">{t('cart.shipping')}</span>
                  <span className="t-body">{shipping === 0 ? t('cart.shippingFree') : formatPrice(shipping)}</span>
                </div>

                {discountApplied ? (
                  <div className="cart-summary__row">
                    <span className="t-body">
                      {t('checkout.discount.applied')} ({discountApplied.code})
                    </span>
                    <span className="t-body">
                      −{formatPrice(discountApplied.discountAmount)}{' '}
                      <button
                        type="button"
                        onClick={handleRemoveDiscount}
                        className="t-small"
                        style={{ textDecoration: 'underline', marginLeft: 'var(--sp-2)' }}
                      >
                        {t('checkout.discount.remove')}
                      </button>
                    </span>
                  </div>
                ) : (
                  <div className="form-group" style={{ margin: 'var(--sp-5) 0' }}>
                    <label className="form-label" htmlFor="discountCode">{t('checkout.discount.label')}</label>
                    <div className="flex gap-2">
                      <input
                        id="discountCode"
                        name="discountCode"
                        className="form-input"
                        value={discountCodeInput}
                        onChange={(e) => { setDiscountCodeInput(e.target.value); if (discountError) setDiscountError(''); }}
                        placeholder={t('checkout.discount.placeholder')}
                        disabled={discountChecking}
                      />
                      <button
                        type="button"
                        className="btn"
                        onClick={handleApplyDiscount}
                        disabled={discountChecking || !discountCodeInput.trim()}
                      >
                        {discountChecking ? t('checkout.discount.checking') : t('checkout.discount.apply')}
                      </button>
                    </div>
                    {discountError && <span className="form-error">{discountError}</span>}
                  </div>
                )}

                <div className="divider" style={{ margin: 'var(--sp-5) 0' }} />

                <div className="cart-summary__row cart-summary__row--total">
                  <span className="t-h4">{t('cart.total')}</span>
                  <span className="t-h4">{formatPrice(total)}</span>
                </div>

                {submitError && (
                  <p className="form-error" style={{ marginTop: 'var(--sp-6)' }}>{submitError}</p>
                )}

                <button
                  type="submit"
                  className="btn btn-lg"
                  disabled={submitting}
                  style={{ width: '100%', justifyContent: 'center', marginTop: 'var(--sp-8)' }}
                >
                  {submitting ? t('checkout.processing') : t('checkout.payButton', { amount: formatPrice(total) })}
                </button>

                <Link to="/carrinho" className="t-small t-light t-center" style={{ display: 'block', marginTop: 'var(--sp-4)', textDecoration: 'underline' }}>
                  {t('checkout.backToCart')}
                </Link>
              </aside>

            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

/* ── Subcomponente PaymentResultInfo ─────────────────────
   Mostra, no ecrã de sucesso, a informação necessária para o
   cliente concluir o pagamento (MB WAY/Multibanco são
   assíncronos — a encomenda existe mas o pagamento ainda não
   está confirmado). O método "cartao" nunca chega aqui: nesse
   caso o browser é redirecionado para o ifthenpay antes de
   mostrar este ecrã.
   ────────────────────────────────────────────────────────── */

function PaymentResultInfo({ t, payment }) {
  if (!payment) return null;

  if (payment.status === 'initiation_failed') {
    return (
      <p className="t-body t-light" style={{ marginTop: 'var(--sp-4)' }}>
        {t('checkout.payment.initiationFailed')}
      </p>
    );
  }

  if (payment.status === 'not_required') {
    return (
      <p className="t-body t-light" style={{ marginTop: 'var(--sp-4)' }}>
        {t('checkout.payment.notRequired')}
      </p>
    );
  }

  if (payment.method === 'mbway') {
    return (
      <p className="t-body t-light" style={{ marginTop: 'var(--sp-4)' }}>
        {t('checkout.payment.mbwayWaiting')}
      </p>
    );
  }

  if (payment.method === 'multibanco') {
    return (
      <div className="checkout-payment-info" style={{ marginTop: 'var(--sp-4)' }}>
        <p className="t-body t-light">{t('checkout.payment.multibancoInstructions')}</p>
        <div className="flex gap-6 justify-center" style={{ marginTop: 'var(--sp-4)', flexWrap: 'wrap' }}>
          <span className="t-body"><strong>{t('checkout.payment.entity')}:</strong> {payment.entity}</span>
          <span className="t-body"><strong>{t('checkout.payment.reference')}:</strong> {payment.reference}</span>
          <span className="t-body"><strong>{t('checkout.payment.amount')}:</strong> {formatPrice(payment.amount)}</span>
        </div>
      </div>
    );
  }

  return null;
}

/* ── Subcomponente Field ───────────────────────────────── */

function Field({ label, name, type = 'text', value, onChange, error, placeholder }) {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={name}>{label}</label>
      <input
        id={name}
        name={name}
        type={type}
        className="form-input"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}
